import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { cn } from './utils';

type Variant = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  variant: Variant;
}

interface Ctx {
  show: (message: string, variant?: Variant) => void;
}

const C = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, variant: Variant = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((arr) => [...arr, { id, message, variant }]);
    setTimeout(() => {
      setToasts((arr) => arr.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <C.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex min-w-72 items-center gap-2 rounded-lg border px-3 py-2 shadow-float backdrop-blur',
              t.variant === 'success' && 'border-status-completed/40 bg-status-completed-tint text-status-completed-on-tint',
              t.variant === 'error' && 'border-error/40 bg-error-tint text-error-on-tint',
              t.variant === 'info' && 'border-border bg-surface text-text',
            )}
            style={{ animation: 'admin-slide-in 180ms ease-out' }}
          >
            {t.variant === 'success' && <CheckCircle2 className="h-4 w-4 shrink-0" />}
            {t.variant === 'error' && <AlertCircle className="h-4 w-4 shrink-0" />}
            <p className="flex-1 text-sm">{t.message}</p>
            <button
              onClick={() => setToasts((arr) => arr.filter((x) => x.id !== t.id))}
              className="-m-1 rounded p-1 hover:bg-black/5 dark:hover:bg-white/10"
              aria-label="dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </C.Provider>
  );
}

export function useToast(): Ctx {
  const v = useContext(C);
  if (!v) throw new Error('useToast needs ToastProvider');
  return v;
}
